/**
=========================================================
* Soft UI Dashboard PRO React - v4.0.3
=========================================================

* Product Page: https://www.creative-tim.com/product/soft-ui-dashboard-pro-react
* Copyright 2024 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useState, useEffect, useRef } from "react";

// react-router components
import { Link } from "react-router-dom";

// Logo assets
import logoStopRasismDark from "assets/images/stop-rasism-1-dark.png";
import logoStopRasismWhite from "assets/images/stop-rasism-2-white.png";

// prop-types is a library for typechecking of props.
import PropTypes from "prop-types";

// @mui material components
import Icon from "@mui/material/Icon";
import Popper from "@mui/material/Popper";
import Paper from "@mui/material/Paper";
import Grow from "@mui/material/Grow";
import ClickAwayListener from "@mui/material/ClickAwayListener";

// Soft UI Dashboard PRO React components
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftButton from "components/SoftButton";

// Soft UI Dashboard PRO React example components
import DefaultNavbarLink from "examples/Navbars/DefaultNavbar/DefaultNavbarLink";
import DefaultNavbarMobile from "examples/Navbars/DefaultNavbar/DefaultNavbarMobile";

// Soft UI Dashboard PRO React base styles
import breakpoints from "assets/theme/base/breakpoints";

// DefaultNavbar dropdown menus
import PagesMenu from "examples/Navbars/DefaultNavbar/Menus/PagesMenu";
import AuthenticationMenu from "examples/Navbars/DefaultNavbar/Menus/AuthenticationMenu";
import EcommerceMenu from "examples/Navbars/DefaultNavbar/Menus/EcommerceMenu";
import ApplicationsMenu from "examples/Navbars/DefaultNavbar/Menus/ApplicationsMenu";
import DocsMenu from "examples/Navbars/DefaultNavbar/Menus/DocsMenu";

const RESURSE_DOC_ITEMS = [
  {
    label: "Strategia națională 2024-2027",
    desc: "Prevenirea antisemitismului și discursului instigator la ură",
    href: "https://cancelarie.gov.ro/wp-content/uploads/2024/07/Monitorul-Oficial-Partea-I-nr.-503Bis.pdf",
  },
  {
    label: "Raport anual 2024-2027",
    desc: "Stadiul implementării Strategiei Naționale",
    href: "https://www.gov.ro/fisiere/stiri_fisiere/Raport_anual_Strategie_2024-2027.pdf",
  },
];

function ResurseDropdownSectionLabel({ children, compact = false }) {
  return (
    <SoftTypography
      variant="caption"
      sx={{
        color: "#1565C0",
        fontWeight: 700,
        textTransform: "uppercase",
        letterSpacing: compact ? "1.2px" : "1.4px",
        fontSize: compact ? "0.65rem" : "0.68rem",
        display: "block",
      }}
    >
      {children}
    </SoftTypography>
  );
}

function ResurseDropdownRow({ title, desc, to, href, onClick, isLast = false, compact = false }) {
  const linkProps = href
    ? { component: "a", href, target: "_blank", rel: "noopener noreferrer" }
    : { component: Link, to };

  return (
    <SoftBox
      {...linkProps}
      onClick={onClick}
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: compact ? 1.5 : 2,
        px: compact ? 2.25 : 2.5,
        py: compact ? 1.25 : 1.75,
        textDecoration: "none",
        cursor: "pointer",
        borderBottom: isLast ? "none" : "1px solid rgba(6, 29, 54, 0.07)",
        transition: "background 0.15s ease",
        "&:hover": { bgcolor: "rgba(6, 29, 54, 0.04)" },
      }}
    >
      <SoftBox flex={1} minWidth={0}>
        <SoftTypography
          sx={{
            color: "#0f2c4a",
            fontWeight: 700,
            fontSize: compact ? "0.84rem" : "0.875rem",
            lineHeight: compact ? 1.3 : 1.35,
            display: "block",
          }}
        >
          {title}
        </SoftTypography>
        <SoftTypography
          sx={{
            color: "#5a6b85",
            fontSize: compact ? "0.72rem" : "0.75rem",
            lineHeight: compact ? 1.4 : 1.5,
            mt: compact ? 0.25 : 0.4,
            display: "block",
          }}
        >
          {desc}
        </SoftTypography>
      </SoftBox>
      <Icon sx={{ color: "#8b9cb0", fontSize: compact ? "1rem !important" : "1.1rem !important", flexShrink: 0 }}>
        {href ? "open_in_new" : "chevron_right"}
      </Icon>
    </SoftBox>
  );
}

function ResurseDropdownContent({ resurseItems, onClose, includeUsefulLinks = false, compact = false }) {
  const sectionPad = compact
    ? { px: 2.25, pt: 1.4, pb: 0.65 }
    : { px: 2.5, pt: 2, pb: 1 };
  const sectionPadDivider = compact
    ? { px: 2.25, pt: 1.6, pb: 0.65 }
    : { px: 2.5, pt: 2.25, pb: 1 };

  return (
    <SoftBox py={compact ? 0.35 : 0.5}>
      {includeUsefulLinks && (
        <>
          <SoftBox {...sectionPad}>
            <ResurseDropdownSectionLabel compact={compact}>Resurse utile</ResurseDropdownSectionLabel>
          </SoftBox>
          {resurseItems.map((item, i) => (
            <ResurseDropdownRow
              key={item.to}
              title={item.label}
              desc={item.desc}
              to={item.to}
              onClick={onClose}
              isLast={i === resurseItems.length - 1}
              compact={compact}
            />
          ))}
        </>
      )}

      <SoftBox
        {...(includeUsefulLinks ? sectionPadDivider : sectionPad)}
        sx={{ borderTop: includeUsefulLinks ? "1px solid rgba(6, 29, 54, 0.07)" : "none" }}
      >
        <ResurseDropdownSectionLabel compact={compact}>Resurse multimedia</ResurseDropdownSectionLabel>
      </SoftBox>
      <ResurseDropdownRow
        title="Resurse multimedia"
        desc="Campanii și materiale informative"
        to="/multimedia"
        onClick={onClose}
        isLast
        compact={compact}
      />

      <SoftBox {...sectionPadDivider} sx={{ borderTop: "1px solid rgba(6, 29, 54, 0.07)" }}>
        <ResurseDropdownSectionLabel compact={compact}>Documente oficiale</ResurseDropdownSectionLabel>
      </SoftBox>
      {RESURSE_DOC_ITEMS.map((doc, i) => (
        <ResurseDropdownRow
          key={doc.href}
          title={doc.label}
          desc={doc.desc}
          href={doc.href}
          onClick={onClose}
          isLast={i === RESURSE_DOC_ITEMS.length - 1}
          compact={compact}
        />
      ))}
    </SoftBox>
  );
}

function DefaultNavbar({ routes, transparent = false, light = false, action = false, logo = null }) {
  const [pagesMenu, setPagesMenu] = useState(false);
  const [authenticationMenu, setAuthenticationMenu] = useState(false);
  const [ecommerceMenu, setEcommerceMenu] = useState(false);
  const [applicationsMenu, setApplicationsMenu] = useState(false);
  const [docsMenu, setDocsMenu] = useState(false);
  const [mobileNavbar, setMobileNavbar] = useState(false);
  const [mobileView, setMobileView] = useState(false);

  const openPagesMenu = ({ currentTarget }) => setPagesMenu(currentTarget);
  const closePagesMenu = () => setPagesMenu(false);
  const openAuthenticationMenu = ({ currentTarget }) => setAuthenticationMenu(currentTarget);
  const closeAuthenticationMenu = () => setAuthenticationMenu(false);
  const openEcommerceMenu = ({ currentTarget }) => setEcommerceMenu(currentTarget);
  const closeEcommerceMenu = () => setEcommerceMenu(false);
  const openApplicationsMenu = ({ currentTarget }) => setApplicationsMenu(currentTarget);
  const closeApplicationsMenu = () => setApplicationsMenu(false);
  const openDocsMenu = ({ currentTarget }) => setDocsMenu(currentTarget);
  const closeDocsMenu = () => setDocsMenu(false);
  const openMobileNavbar = ({ currentTarget }) => setMobileNavbar(currentTarget.parentNode);
  const closeMobileNavbar = () => setMobileNavbar(false);

  const [resurseAnchor, setResurseAnchor] = useState(null);
  const resurseOpen = Boolean(resurseAnchor);
  const openResurse = (e) => setResurseAnchor(e.currentTarget);
  const closeResurse = () => setResurseAnchor(null);


  const resurseItems = [
    {
      label: "Ghid de utilizare",
      desc: "Cum să completezi formularul și să urmărești statusul sesizării",
      icon: "menu_book",
      to: "/ghid",
      color: "#344767",
    },
    {
      label: "Bibliotecă de informații",
      desc: "Informații esențiale înainte de a depune o sesizare",
      icon: "lightbulb",
      to: "/ce-trebuie-sa-stii",
      color: "#1565C0",
    },
    {
      label: "Drepturile victimelor",
      desc: "Drepturile tale în calitate de victimă a infracțiunilor",
      icon: "gavel",
      to: "/drepturile-victimelor",
      color: "#6a1b9a",
    },
  ];

  useEffect(() => {
    // A function that sets the display state for the DefaultNavbarMobile.
    function displayMobileNavbar() {
      if (window.innerWidth < breakpoints.values.lg) {
        setMobileView(true);
        setMobileNavbar(false);
      } else {
        setMobileView(false);
        setMobileNavbar(false);
      }
    }

    /** 
     The event listener that's calling the displayMobileNavbar function when 
     resizing the window.
    */
    window.addEventListener("resize", displayMobileNavbar);

    // Call the displayMobileNavbar function to set the state with the initial value.
    displayMobileNavbar();

    // Remove event listener on cleanup
    return () => window.removeEventListener("resize", displayMobileNavbar);
  }, []);

  // OUTER FULL-WIDTH BAND — pe desktop oferă fundalul navy edge-to-edge (fără
  // zone albe în stânga/dreapta/sus/jos). Pe mobile bg-ul vine de la SoftBox-ul
  // interior (alb, ca înainte).
  return (
    <SoftBox
      sx={{
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        width: "100%",
        zIndex: 3,
        bgcolor: { xs: "transparent", md: "#061D36" },
      }}
    >
      <SoftBox
        // Pe mobile: full-width fără padding (ca RESURSE bar să fie dintr-un capăt
        //   în altul, ca înainte). Pe desktop: limităm conținutul cu maxWidth+mx auto
        //   ca să rămână frumos pe ecrane late, fundalul navy fiind dat de wrapper-ul
        //   exterior care e full-width.
        maxWidth={{ xs: "100%", md: "1400px" }}
        mx={{ xs: 0, md: "auto" }}
        // Pe desktop: padding vertical simetric. Pe mobile: padding doar pe rândul logo+butoane.
        pt={{ xs: 0, md: 1.75 }}
        pb={{ xs: 0, md: 1.75 }}
        // Padding orizontal mai generos pe tabletă/desktop ca logo-ul să respire.
        px={{ xs: 0, sm: transparent ? 2 : 5, md: 5, lg: 8 }}
        my={0}
        width="100%"
        shadow={transparent ? "none" : { xs: "md", md: "none" }}
        color={light ? "white" : "dark"}
        display="flex"
        flexDirection={{ xs: "column", md: "row" }}
        justifyContent={{ xs: "flex-start", md: "space-between" }}
        alignItems={{ xs: "stretch", md: "center" }}
        sx={({ palette: { transparent: transparentColor, white } }) => ({
          // Pe mobile (xs/sm): alb cu shadow ca înainte (UI-ul vechi neatins).
          // Pe desktop (md+): transparent — fundalul navy vine de la wrapper-ul exterior.
          backgroundColor: {
            xs: transparent ? transparentColor.main : white.main,
            md: "transparent",
          },
          backdropFilter: transparent ? "none" : { xs: `saturate(200%) blur(30px)`, md: "none" },
          overflow: "hidden",
          borderRadius: "0px",
        })}
      >
        {/* ── MOBILE ONLY: RESURSE bar flush at top ── */}
        <SoftBox display={{ xs: "block", md: "none" }}>
          <SoftBox
            onClick={resurseOpen ? closeResurse : openResurse}
            display="flex"
            alignItems="center"
            justifyContent="center"
            py={1.1}
            sx={{ bgcolor: "#3d5175", cursor: "pointer", userSelect: "none", position: "relative" }}
          >
            <SoftBox display="flex" alignItems="center" gap={1}>
              <Icon sx={{ color: "rgba(255,255,255,0.8)", fontSize: "1rem !important" }}>library_books</Icon>
              <SoftTypography sx={{ color: "#ffffff !important", fontWeight: 700, fontSize: "0.78rem", textTransform: "uppercase", letterSpacing: "1px" }}>
                Resurse
              </SoftTypography>
            </SoftBox>
            <Icon sx={{
              color: "rgba(255,255,255,0.7)",
              fontSize: "1.1rem !important",
              transition: "transform 0.25s ease",
              transform: resurseOpen ? "rotate(180deg)" : "rotate(0deg)",
              position: "absolute",
              right: 16,
            }}>
              expand_more
            </Icon>
          </SoftBox>
        </SoftBox>

        {/* ── Main row: logo + buttons ── */}
        <SoftBox
          display="flex"
          flexDirection="row"
          justifyContent="space-between"
          alignItems="center"
          flex={{ xs: "0 0 auto", md: 1 }}
          width={{ xs: "94%", md: "auto" }}
          mx={{ xs: "auto", md: 0 }}
          py={{ xs: 1.25, md: 0 }}
          gap={{ xs: 0.5, md: 0 }}
          sx={{ minWidth: 0 }}
        >
        <SoftBox
          component={Link}
          to="/"
          lineHeight={1}
          sx={{
            display: { xs: "block", md: "flex" },
            alignItems: { md: "center" },
            mr: { md: "auto" },
            flex: { xs: "1 1 0", md: "0 0 auto" },
            minWidth: 0,
            pr: { xs: 0.5, md: 0 },
          }}
        >
          {/* Mobile (header alb): stop-rasism-1-dark-varianta2. Desktop (header navy): stop-rasism-2-white-varianta2. */}
          {logo ? (
            <SoftBox
              component="img"
              src={logo}
              alt="Stop Rasism Logo"
              sx={{
                height: { xs: "36px", md: "86px" },
                display: "block",
                maxWidth: { xs: "100%", md: "none" },
                width: "auto",
                objectFit: "contain",
                objectPosition: "left center",
              }}
            />
          ) : (
            <>
              <SoftBox
                component="img"
                src={logoStopRasismDark}
                alt="Stop Rasism Logo"
                sx={{
                  height: "36px",
                  maxWidth: "100%",
                  width: "auto",
                  objectFit: "contain",
                  objectPosition: "left center",
                  display: { xs: "block", md: "none" },
                }}
              />
              <SoftBox
                component="img"
                src={logoStopRasismWhite}
                alt="Stop Rasism Logo"
                sx={{
                  height: "50px",
                  display: { xs: "none", md: "block" },
                }}
              />
            </>
          )}
        </SoftBox>
          {/* ── Link-uri text directe — desktop ≥1025px; pe tabletă (≤1024px) sunt în dropdown ── */}
          <SoftBox
            display="none"
            alignItems="center"
            gap={{ lg: 0.5, xl: 1 }}
            mr={{ lg: 1.5, xl: 2.5 }}
            sx={{ "@media (min-width: 1025px)": { display: "flex" } }}
          >
            {resurseItems.map((item) => (
              <SoftBox
                key={item.to}
                component={Link}
                to={item.to}
                sx={{
                  color: "rgba(255,255,255,0.88)",
                  fontSize: { md: "0.78rem", lg: "0.85rem" },
                  fontWeight: 500,
                  textDecoration: "none",
                  px: { md: 1.25, lg: 1.75 },
                  py: 1,
                  borderRadius: "8px",
                  whiteSpace: "nowrap",
                  transition: "color 0.2s ease, background 0.2s ease",
                  "&:hover": {
                    color: "#FFC107",
                    background: "rgba(255,255,255,0.06)",
                  },
                }}
              >
                {item.label}
              </SoftBox>
            ))}
          </SoftBox>

          {/* Right button group */}
          <SoftBox
            display="flex"
            flexDirection="row"
            alignItems={{ xs: "center", md: "stretch" }}
            flexShrink={0}
            ml={{ xs: 0, md: 0 }}
            sx={{
              minWidth: 0,
            }}
          >
            {/* ── RESURSE dropdown — desktop only (mobile moved to top bar) ── */}
            <SoftBox position="relative" display={{ xs: "none", md: "block" }}>
              <SoftButton
              onClick={resurseOpen ? closeResurse : openResurse}
              sx={{
                fontSize: { xs: "0.55em", sm: "0.65em", md: "0.78em" },
                background: "#1a3a5c",
                color: "white",
                borderTopLeftRadius: { xs: "8px", md: "55px" },
                borderTopRightRadius: { xs: "8px", md: 0 },
                borderBottomLeftRadius: { xs: 0, md: "55px" },
                borderBottomRightRadius: { xs: 0, md: 0 },
                borderRight: { md: "1px solid rgba(255,255,255,0.14)" },
                padding: { xs: "8px 10px", sm: "8px 12px", md: "11px 20px" },
                paddingRight: { md: "1.6em" },
                marginLeft: { md: 0 },
                fontWeight: "bold",
                textTransform: "uppercase",
                letterSpacing: "0.3px",
                whiteSpace: "nowrap",
                minHeight: "auto",
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                width: "100%",
                boxShadow: "none",
                "&:hover": { background: "#234a72", boxShadow: "none" },
                "&:focus": { background: "#1a3a5c !important", boxShadow: "none !important" },
                "&:focus:not(:hover)": { background: "#1a3a5c !important", boxShadow: "none !important" },
                "&:active": { background: "#234a72 !important", boxShadow: "none !important" },
                "&.Mui-focusVisible": { background: "#1a3a5c !important", boxShadow: "none !important" },
                "& .MuiTouchRipple-root": { display: "none" },
              }}
            >
              <Icon sx={{ fontSize: "1rem !important" }}>library_books</Icon>
              Resurse
              <Icon
                sx={{
                  fontSize: "1.1rem !important",
                  ml: 0.25,
                  transition: "transform 0.25s ease",
                  transform: resurseOpen ? "rotate(180deg)" : "rotate(0deg)",
                }}
              >
                expand_more
              </Icon>
              </SoftButton>

            <Popper
              open={resurseOpen}
              anchorEl={resurseAnchor}
              placement="bottom-start"
              transition
              style={{ zIndex: 1300 }}
            >
              {({ TransitionProps }) => (
                <Grow {...TransitionProps} style={{ transformOrigin: "top left" }}>
                  <Paper
                    elevation={0}
                    sx={{
                      mt: { xs: 0, md: 1.5 },
                      borderRadius: { xs: "0 0 14px 14px", md: "12px" },
                      overflow: { xs: "hidden", md: "visible" },
                      width: { xs: "100vw", md: "auto" },
                      minWidth: { xs: "auto", md: 360 },
                      maxWidth: { md: 420 },
                      bgcolor: "#fff",
                      border: "1px solid rgba(6, 29, 54, 0.08)",
                      boxShadow: {
                        xs: "0 8px 24px rgba(0,0,0,0.12)",
                        md: "0 14px 40px rgba(6, 29, 54, 0.14)",
                      },
                      position: "relative",
                      "&::before": {
                        content: '""',
                        display: { xs: "none", md: "block" },
                        position: "absolute",
                        top: -7,
                        left: 28,
                        width: 14,
                        height: 14,
                        bgcolor: "#fff",
                        borderLeft: "1px solid rgba(6, 29, 54, 0.08)",
                        borderTop: "1px solid rgba(6, 29, 54, 0.08)",
                        transform: "rotate(45deg)",
                      },
                    }}
                  >
                    <ClickAwayListener onClickAway={closeResurse}>
                      <SoftBox>
                        <SoftBox sx={{ display: "none", "@media (min-width: 1025px)": { display: "block" } }}>
                          <ResurseDropdownContent
                            resurseItems={resurseItems}
                            onClose={closeResurse}
                            includeUsefulLinks={false}
                          />
                        </SoftBox>
                        <SoftBox sx={{ display: "block", "@media (min-width: 1025px)": { display: "none" } }}>
                          <ResurseDropdownContent
                            resurseItems={resurseItems}
                            onClose={closeResurse}
                            includeUsefulLinks
                            compact
                          />
                        </SoftBox>
                      </SoftBox>
                    </ClickAwayListener>
                  </Paper>
                </Grow>
              )}
            </Popper>
            </SoftBox>

          {/* ── VERIFICAȚI STATUS — same medium navy as RESURSE ── */}
          <SoftButton
            component={Link}
            to="/status-sesizare"
            sx={{
              fontSize: { xs: "0.56em", md: "0.78em" },
              background: "#1a3a5c !important",
              color: "white !important",
              borderTopLeftRadius: { xs: "8px", md: 0 },
              borderTopRightRadius: 0,
              borderBottomLeftRadius: { xs: "8px", md: 0 },
              borderBottomRightRadius: 0,
              padding: { xs: "7px 12px", md: "11px 24px" },
              fontWeight: "bold",
              textTransform: "uppercase",
              letterSpacing: "0.3px",
              whiteSpace: "nowrap",
              minHeight: "auto",
              display: "flex",
              alignItems: "center",
              boxShadow: "none",
              "&:hover": {
                background: "#234a72 !important",
                boxShadow: "none !important",
              },
              "&:focus, &:focus:not(:hover)": {
                background: "#1a3a5c !important",
                boxShadow: "none !important",
              },
            }}
          >
            STATUS
          </SoftButton>

          {/* ── TRIMITE SESIZARE — yellow CTA ── */}
              <SoftButton
            component={Link}
            to="/formular-sesizare"
            sx={{
              background: "#FFC107 !important",
              color: "#061D36 !important",
              borderTopLeftRadius: 0,
              borderTopRightRadius: { xs: "8px", md: "55px" },
              borderBottomLeftRadius: 0,
              borderBottomRightRadius: { xs: "8px", md: "55px" },
              fontSize: { xs: "0.56em", md: "0.78em" },
              padding: { xs: "7px 14px", md: "11px 26px" },
              fontWeight: "bold",
              textTransform: "uppercase",
              letterSpacing: "0.3px",
              whiteSpace: "nowrap",
              minHeight: "auto",
              display: "flex",
              alignItems: "center",
              boxShadow: "0 4px 14px rgba(255,193,7,0.25)",
              "&:hover": {
                background: "#FFB300 !important",
                color: "#061D36 !important",
                boxShadow: "0 6px 18px rgba(255,193,7,0.35) !important",
              },
              "&:focus, &:focus:not(:hover)": {
                background: "#FFC107 !important",
                color: "#061D36 !important",
                boxShadow: "0 0 0 3px rgba(255,193,7,0.4) !important",
              },
            }}
          >
            TRIMITE SESIZARE
              </SoftButton>
            </SoftBox>{/* end right button group */}
        </SoftBox>{/* end main row */}

      </SoftBox>{/* end inner navbar row */}
      <PagesMenu routes={routes} open={pagesMenu} close={closePagesMenu} />
      <AuthenticationMenu
        routes={routes}
        open={authenticationMenu}
        close={closeAuthenticationMenu}
      />
      <EcommerceMenu routes={routes} open={ecommerceMenu} close={closeEcommerceMenu} />
      <ApplicationsMenu routes={routes} open={applicationsMenu} close={closeApplicationsMenu} />
      <DocsMenu routes={routes} open={docsMenu} close={closeDocsMenu} />
      {mobileView && (
        <></>
        // <DefaultNavbarMobile routes={routes} open={mobileNavbar} close={closeMobileNavbar} />
      )}
    </SoftBox>
  );
}


// Typechecking props for the DefaultNavbar
DefaultNavbar.propTypes = {
  routes: PropTypes.arrayOf(PropTypes.object).isRequired,
  transparent: PropTypes.bool,
  light: PropTypes.bool,
  logo: PropTypes.string,
  action: PropTypes.oneOfType([
    PropTypes.bool,
    PropTypes.shape({
      type: PropTypes.oneOf(["external", "internal"]).isRequired,
      route: PropTypes.string.isRequired,
      color: PropTypes.oneOf([
        "primary",
        "secondary",
        "info",
        "success",
        "warning",
        "error",
        "dark",
        "light",
      ]),
      label: PropTypes.string.isRequired,
    }),
  ]),
};

export default DefaultNavbar;
